import { components as c } from "@/components";
import { EVENT_TYPES } from "@/features/events/definitions/event-types";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import type { TrackingType } from "@/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

export const EventsForm = () => {
	const { required_events, setEvents } = useTemplateStore();
	const {
		steps,
		currentStep,
		markStepComplete,
		nextStep,
		updateEventSteps,
		setCurrentStep,
	} = useTemplateNavigationStore();
	const navigate = useNavigate();
	const { templateId } = Route.useParams();

	// Set this page as the current step
	useEffect(() => {
		setCurrentStep("events"); // Events is the third step (index 2)
	}, [setCurrentStep]);

	useEffect(() => {
		if (required_events.length > 0) {
			markStepComplete("events");
			updateEventSteps(required_events);
		}
	}, [required_events, markStepComplete, updateEventSteps]);

	const handleEventChange = (eventType: TrackingType) => {
		// For radio buttons, we want to replace the entire array with just the selected event
		setEvents([eventType]);
	};

	const handleNext = () => {
		if (required_events.length > 0) {
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			const next = steps[currentIndex + 1];
			if (
				next && next.route && typeof next.route.to === "string" &&
				typeof next.route.params === "function"
			) {
				nextStep();
				navigate({
					to: next.route.to,
					params: next.route.params({ templateId }),
				});
			}
		}
	};

	const handlePrevious = () => {
		navigate({ to: "/new/diary/$templateId/trackers" });
	};

	return (
		<div className="space-y-8">
			<c.ui.Card>
				<c.ui.CardHeader>
					<c.ui.CardTitle>Intervention</c.ui.CardTitle>
				</c.ui.CardHeader>
				<c.ui.CardContent>
					{EVENT_TYPES.map((event) => (
						<div
							key={event.value}
							className="flex items-center space-x-1"
						>
							<c.ui.RadioGroup
								value={required_events[0] || ""}
								onValueChange={(value) =>
									handleEventChange(value as TrackingType)}
								className="flex items-center space-x-2 hover:bg-slate-100/80 p-2 rounded-lg w-full"
							>
								<div className="flex items-center space-x-2 hover:bg-accent p-2 rounded-md cursor-pointer">
									<c.ui.RadioGroupItem
										value={event.value}
										id={event.value}
									/>
									<c.ui.Label
										htmlFor={event.value}
										className="cursor-pointer"
									>
										{event.label}
									</c.ui.Label>
								</div>
							</c.ui.RadioGroup>
						</div>
					))}
				</c.ui.CardContent>
			</c.ui.Card>

			{/* Navigation Buttons */}
			<div className="flex justify-between gap-4 mt-8">
				<c.ui.Button
					type="button"
					variant="outline"
					onClick={handlePrevious}
				>
					Previous
				</c.ui.Button>

				<c.ui.Button
					type="button"
					onClick={handleNext}
					disabled={required_events.length === 0}
				>
					Next
				</c.ui.Button>
			</div>
		</div>
	);
};

export const Route = createFileRoute("/_app/new/diary/$templateId/events/")({
	component: EventsForm,
});
